"use client";

import { c as _c } from "react/compiler-runtime";
import type { Review } from "@justhome/api-website";
import { StarsRating } from "@justhome/react-ui";
import { useFormatter, useLocale, useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
const SHOWN = 3;
const CLAMP = "line-clamp-6";
const Stars = t0 => {
  const $ = _c(8);
  const {
    rating
  } = t0;
  const t = useTranslations("homepage-v2");
  let t1;
  if ($[0] !== rating || $[1] !== t) {
    t1 = t("rating_stars", {
      rating: Math.round(rating)
    });
    $[0] = rating;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== rating) {
    t2 = <StarsRating stars={rating} />;
    $[3] = rating;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <span role="img" aria-label={t1}>{t2}</span>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
const initials = (name: string) => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = words[0]?.[0] ?? "";
  const last = words.length > 1 ? words[words.length - 1]?.[0] ?? "" : "";
  return (first + last).toUpperCase();
};
const Card = t0 => {
  const $ = _c(38);
  const {
    review
  } = t0;
  const t = useTranslations("homepage-v2");
  const format = useFormatter();
  const locale = useLocale();
  const quote = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [clipped, setClipped] = useState(false);
  const [failed, setFailed] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const element = quote.current;
      if (!element) {
        return;
      }
      const measure = () => setClipped(element.scrollHeight > element.clientHeight + 1);
      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(element);
      return () => observer.disconnect();
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] !== format || $[3] !== review.submittedAt) {
    const date = new Date(review.submittedAt);
    t3 = Number.isNaN(date.getTime()) ? null : format.relativeTime(date);
    $[2] = format;
    $[3] = review.submittedAt;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const written = t3;
  const translated = !!review.language && review.language !== locale;
  let t4;
  if ($[5] !== review.rating) {
    t4 = <Stars rating={review.rating} />;
    $[5] = review.rating;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== review.submittedAt || $[8] !== written) {
    t5 = written && <time dateTime={review.submittedAt} className="text-caption-1 font-book text-neutral-425">{written}</time>;
    $[7] = review.submittedAt;
    $[8] = written;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4 || $[11] !== t5) {
    t6 = <div className="flex items-center justify-between gap-2xs">{t4}{t5}</div>;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = `text-body-2 font-book text-neutral-425 mt-2xs m-none ${expanded ? "" : CLAMP}`;
  let t8;
  if ($[13] !== review.text || $[14] !== t7) {
    t8 = <blockquote ref={quote} className={t7}>„{review.text}“</blockquote>;
    $[13] = review.text;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t || $[17] !== translated) {
    t9 = translated && <p className="text-caption-1 font-book text-neutral-425 mt-3xs m-none">{t("reviews_translated")}</p>;
    $[16] = t;
    $[17] = translated;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== clipped || $[20] !== expanded || $[21] !== t) {
    t10 = clipped && !expanded && <button type="button" onClick={() => setExpanded(true)} className="self-start mt-3xs p-none bg-transparent border-none cursor-pointer text-body-2 font-semibold text-primary-800 hover:text-primary-825 focusable">{t("reviews_more")}</button>;
    $[19] = clipped;
    $[20] = expanded;
    $[21] = t;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== failed || $[24] !== review.authorName || $[25] !== review.authorPhotoUrl) {
    t11 = review.authorPhotoUrl && !failed ? <img src={review.authorPhotoUrl} alt="" referrerPolicy="no-referrer" loading="lazy" onError={() => setFailed(true)} className="w-[32px] h-[32px] rounded-full object-cover flex-none bg-neutral-12" /> : <span aria-hidden="true" className="w-[32px] h-[32px] rounded-full bg-neutral-12 text-caption-1 font-semibold text-neutral-425 flex items-center justify-center flex-none">{initials(review.authorName)}</span>;
    $[23] = failed;
    $[24] = review.authorName;
    $[25] = review.authorPhotoUrl;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== review.authorName) {
    t12 = <span className="text-body-2 font-semibold">{review.authorName}</span>;
    $[27] = review.authorName;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t11 || $[30] !== t12) {
    t13 = <figcaption className="flex items-center gap-2xs mt-auto pt-2s">{t11}{t12}</figcaption>;
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t10 || $[33] !== t13 || $[34] !== t6 || $[35] !== t8 || $[36] !== t9) {
    t14 = <li><figure className="h-full bg-neutral-0 border border-neutral-50 rounded-m p-3s m-none flex flex-col">{t6}{t8}{t9}{t10}{t13}</figure></li>;
    $[32] = t10;
    $[33] = t13;
    $[34] = t6;
    $[35] = t8;
    $[36] = t9;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  return t14;
};
const ReviewCards = t0 => {
  const $ = _c(12);
  const {
    reviews
  } = t0;
  const t = useTranslations("homepage-v2");
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== reviews || $[1] !== t) {
    t4 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const shown = reviews.slice(0, SHOWN);
      if (!shown.length) {
        t4 = null;
        break bb0;
      }
      t1 = "grid gap-3s m:grid-cols-3 items-stretch list-none p-none m-none";
      if ($[6] !== t) {
        t2 = t("reviews_group");
        $[6] = t;
        $[7] = t2;
      } else {
        t2 = $[7];
      }
      t3 = shown.map(_temp);
    }
    $[0] = reviews;
    $[1] = t;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
  }
  if (t4 !== Symbol.for("react.early_return_sentinel")) {
    return t4;
  }
  let t5;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t5 = <ul className={t1} aria-label={t2}>{t3}</ul>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
};
export default ReviewCards;
function _temp(review) {
  return <Card key={`${review.authorName}-${review.submittedAt}`} review={review} />;
}